/**
 * Saved lab report on the lab desk (PROD-3204, PM slice 5, board N5): the current version, read only, with where its
 * values came from, the report's facts, and the values grouped by ordered test. "Amend" asks which value to change,
 * then opens the Amend dialog (N6). Behind `?x=intake-desk`.
 *
 * Pieces: the app `Dialog` at `--dialog-width-large`, a soft `BridgeStatusChip`, the app `Table`, 32 px outline icon
 * buttons with an `aria-label`, and the app `Button` (View report and Amend outline, Close primary and last).
 */
import { useState } from 'react';
import { Pencil } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { BridgeStatusChip } from '@/components/ui/status-chip';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useLabRequestReports } from '@/scheduling/services/use-lab-request-reports';
import {
  canAmend,
  collectedText,
  currentReport,
  flagText,
  performingLabText,
  provenanceLine,
  rangeText,
  reportedText,
  reportGroups,
  resultsCount,
  testName,
  unitText,
  valueText,
  versionLabel,
} from '@/scheduling/lib/saved-lab-report';
import type { LabReportObservationDto } from '@/types/lab-results-api';
import type { PatientLabOrderDetail } from '@/types/scheduling-api';
import { AmendLabReportDialog } from './amend-lab-report-dialog';

export interface SavedLabReportDialogProps {
  /** The desk row whose report is shown; null closes the dialog. */
  row: PatientLabOrderDetail | null;
  onClose: () => void;
}

const COLUMNS = ['Test', 'Result', 'Unit', 'Range as printed', 'Flag as printed'];

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-1">
      <dt className="text-text-secondary [font:var(--type-meta)]">{label}</dt>
      <dd className="m-0 text-text-primary [font:var(--type-body)]">{value}</dd>
    </div>
  );
}

export function SavedLabReportDialog({ row, onClose }: SavedLabReportDialogProps) {
  const patientId = row?.patientId ?? '';
  const labRequestId = row?.id ?? '';
  const { data, isLoading, isError, refetch } = useLabRequestReports(patientId, labRequestId, row !== null);
  const [picking, setPicking] = useState(false);
  const [amending, setAmending] = useState<LabReportObservationDto | null>(null);
  const report = currentReport(data?.reports);
  const amendable = !!report && canAmend(report);

  const close = () => {
    setPicking(false);
    setAmending(null);
    onClose();
  };

  return (
    <>
      <Dialog open={row !== null && amending === null} onOpenChange={(open) => (open ? undefined : close())}>
        <DialogContent className="max-w-[calc(100%-2rem)] sm:max-w-[var(--dialog-width-large)]">
          <DialogHeader>
            <div className="flex flex-wrap items-center gap-2">
              <DialogTitle className="[font:var(--type-card-title)]">Lab report · {row?.patientName ?? ''}</DialogTitle>
              {report ? <BridgeStatusChip semantic="neutral">{versionLabel(report)}</BridgeStatusChip> : null}
            </div>
            <DialogDescription className="text-text-secondary [font:var(--type-body)]">
              {report ? provenanceLine(report) : row?.orderName ?? ''}
            </DialogDescription>
          </DialogHeader>

          {isLoading ? (
            <div className="flex flex-col gap-2" aria-busy>
              <Skeleton className="h-6 w-full" />
              <Skeleton className="h-6 w-full" />
              <Skeleton className="h-6 w-2/3" />
            </div>
          ) : isError ? (
            <div role="alert" className="flex items-center gap-3">
              <span className="text-[var(--status-bad)] [font:var(--type-body)]">The report could not be loaded.</span>
              <Button variant="outline" size="sm" onClick={() => void refetch()}>
                Try again
              </Button>
            </div>
          ) : !report ? (
            <p className="m-0 text-text-secondary [font:var(--type-body)]">No values are saved for this request.</p>
          ) : (
            <div className="flex max-h-[70vh] flex-col gap-4 overflow-y-auto">
              {report.version > 1 && report.amendReason ? (
                <p className="m-0 text-text-secondary [font:var(--type-body)]">
                  Version {report.version} reason: {report.amendReason}
                </p>
              ) : null}
              <dl className="m-0 grid grid-cols-2 gap-4 sm:grid-cols-5">
                <Fact label="Performing lab" value={performingLabText(report)} />
                <Fact label="Collected" value={collectedText(report)} />
                <Fact label="Reported" value={reportedText(report)} />
                <Fact label="Ordered by" value={row?.orderReqBy?.trim() || '—'} />
                <Fact label="Encounter" value={row?.encounterNumber?.trim() || '—'} />
              </dl>
              {picking ? (
                <p className="m-0 text-text-secondary [font:var(--type-body)]">Choose the value to change.</p>
              ) : null}
              <Table>
                <TableHeader>
                  <TableRow>
                    {COLUMNS.map((c) => (
                      <TableHead key={c}>{c}</TableHead>
                    ))}
                    {picking ? <TableHead className="text-right">Amend</TableHead> : null}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reportGroups(report, data?.tests).map((g) => [
                    <TableRow key={`g-${g.key}`}>
                      <TableCell colSpan={COLUMNS.length + (picking ? 1 : 0)}>
                        <span className="text-text-primary [font:var(--type-label)]">{g.title}</span>
                        <span className="ml-2 text-text-muted [font:var(--type-meta)]">{resultsCount(g.rows.length)}</span>
                      </TableCell>
                    </TableRow>,
                    ...g.rows.map((o) => (
                      <TableRow key={o.id} data-testid={`saved-value-${o.id}`}>
                        <TableCell>{testName(o)}</TableCell>
                        <TableCell className="tabular-nums">{valueText(o)}</TableCell>
                        <TableCell>{unitText(o)}</TableCell>
                        <TableCell className="tabular-nums">{rangeText(o)}</TableCell>
                        <TableCell>{flagText(o)}</TableCell>
                        {picking ? (
                          <TableCell className="text-right">
                            <Button
                              variant="outline"
                              size="icon-sm"
                              aria-label={`Amend ${testName(o)}`}
                              onClick={() => setAmending(o)}
                            >
                              <Pencil aria-hidden />
                            </Button>
                          </TableCell>
                        ) : null}
                      </TableRow>
                    )),
                  ])}
                </TableBody>
              </Table>
            </div>
          )}

          <DialogFooter>
            {row?.documentUrl ? (
              <Button variant="outline" onClick={() => window.open(row.documentUrl ?? '', '_blank', 'noopener,noreferrer')}>
                View report
              </Button>
            ) : null}
            {amendable ? (
              <Button variant="outline" aria-pressed={picking} onClick={() => setPicking((p) => !p)}>
                {picking ? 'Stop amending' : 'Amend'}
              </Button>
            ) : null}
            <Button onClick={close}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {report && row ? (
        <AmendLabReportDialog
          patientId={patientId}
          labRequestId={labRequestId}
          report={report}
          observation={amending}
          onClose={() => setAmending(null)}
          onSaved={() => {
            setAmending(null);
            setPicking(false);
          }}
        />
      ) : null}
    </>
  );
}
